import { useMutation, useQuery } from '@tanstack/react-query';
import { announceSavedOffline } from '@/stores/savedOffline';
import { isOfflineGap, NOT_ON_THIS_PC } from '@/offline/availability';
import { useState } from 'react';
import { api, UNREACHABLE_MESSAGE } from '@/api/client';
import type { components } from '@/api/schema';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import { DocumentFrame } from '@/components/DocumentFrame';
import { LoadingNote } from '@/components/OfflineDetails';
import { AppIcon } from '@/components/AppIcon';
import { HistoryPanel } from '@/components/documents';
import { DemandDecisionDialog, type DemandDecision } from '@/components/stock-actions/DemandDecisionDialog';
import { DemandDialog } from '@/components/stock-actions/DemandDialog';
import { ErrorNote, OkNote, Pager, Select, StatusPill, Tile, errorMessage, quantity, shortDate } from '@/components/ui';
import { useStockInvalidation } from '@/hooks/inventory';
import { KEPT_ON_THIS_PC } from '@/hooks/stockSend';
import { saveWork, takeBack } from '@/offline/engine';
import type { Kind } from '@/offline/outbox';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { useScreenIntent } from '@/stores/shellNav';

type DemandRow = components['schemas']['DemandItemDto'];

const PAGE_SIZE = 50;

const FILTERS = [
  { value: 'open', label: 'Open (waiting or approved)' },
  { value: 'PENDING', label: 'Waiting for a decision' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'FULFILLED', label: 'Fulfilled' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Withdrawn' },
  { value: '', label: 'All' },
];

/** "Cancelled" reads as if someone else stopped it; it was withdrawn by whoever raised it. */
function DemandStatus({ status }: { status: string }) {
  if (status === 'CANCELLED') {
    return (
      <span className="pill">
        <AppIcon kind="statusRefused" size={12} />
        Withdrawn
      </span>
    );
  }
  if (status === 'PENDING') {
    return (
      <span className="pill pill--warn">
        <AppIcon kind="statusPending" size={12} />
        Waiting
      </span>
    );
  }
  return <StatusPill status={status} />;
}

/** "80 BAG", the way a demand's amounts read. */
function amountOf(r: DemandRow, qty: string) {
  return `${quantity(qty, r.uomPrecision)} ${r.uom}`;
}

/** What was said on deciding, as it reads on a hover: the reason for a reject. */
function decisionTitle(r: DemandRow): string | undefined {
  if (!r.decisionComment || r.status === 'CANCELLED') return undefined;
  return `${r.status === 'REJECTED' ? 'Reason' : 'Comment'}: ${r.decisionComment}`;
}

/** "rejected: Enough stock on hand." — what was typed, ending as a sentence. */
function withReason(words: string, reason: string | undefined) {
  if (!reason) return `${words}.`;
  return `${words}: ${reason}${/[.!?]$/.test(reason) ? '' : '.'}`;
}

/**
 * Stock demands: what stores have asked for, and the decision on each.
 *
 * Raised mostly from a low-stock row on the Stock screen. Whoever raised a
 * demand can withdraw it until someone decides; someone else approves or
 * rejects it, and marks it fulfilled once the stock has come in.
 */
export function Demands() {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const [status, setStatus] = useState('open');
  const [skip, setSkip] = useState(0);
  const intent = useScreenIntent('inv-demand');
  const [selectedKey, setSelectedKey] = useState<string | null>(intent?.recordId ?? null);
  // Sent here to open one demand, by its id.
  const [openKey, setOpenKey] = useState<string | null>(() => intent?.recordId ?? null);
  const [note, setNote] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  // Approving or rejecting asks first: an amount to approve, a reason to reject.
  const [deciding, setDeciding] = useState<{ mode: 'approve' | 'reject'; row: DemandRow } | null>(null);

  const list = useQuery({
    queryKey: ['inv', 'demands', farmIds, status, skip],
    queryFn: () =>
      api.get('/api/v1/inventory/demands', {
        take: PAGE_SIZE,
        skip,
        ...(status ? { status } : {}),
        ...farmScopeQuery(farmIds),
      }),
  });
  const { data, isPending, error } = list;

  function done(message: string) {
    invalidate();
    setNote(message);
  }

  /**
   * One decision through the outbox (.docs/023): answered at once with a line,
   * kept on this PC and sent by itself without one. A demand cannot be edited,
   * so its status alone is what the server checks the decision against.
   */
  type Decided = { row: DemandRow; body?: DemandDecision };
  const decide = (verb: 'approve' | 'reject' | 'fulfil' | 'cancel', words: string) => async ({ row: r, body }: Decided) => {
    const saved = await saveWork([
      {
        kind: `demand.${verb}` as Kind,
        method: 'POST',
        path: `/api/v1/inventory/demands/${r.id}/${verb}`,
        ...(verb === 'cancel' ? {} : { body: body ?? {} }),
        describes: `${words} ${r.documentNo}`,
        screen: 'inv-demand',
      },
    ]);
    if (saved.outcome === 'waiting') void announceSavedOffline({ what: `${words} ${r.documentNo}`, closeLabel: 'OK' });
    if (saved.outcome === 'sent' || saved.outcome === 'waiting') return saved.outcome;
    for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
    throw new Error(saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
  };
  const said = (outcome: 'sent' | 'waiting', words: string) => done(outcome === 'sent' ? words : KEPT_ON_THIS_PC);

  const approve = useMutation({
    mutationFn: decide('approve', 'Approving'),
    onSuccess: (outcome, { row: r, body }) => {
      const asked = r.qty ? ` (asked ${amountOf(r, r.qty)})` : '';
      const as = body?.qty ? ` for ${amountOf(r, body.qty)}${asked}` : '';
      said(outcome, `${r.documentNo} approved${as}. Mark it fulfilled once the stock arrives.`);
    },
  });
  const reject = useMutation({
    mutationFn: decide('reject', 'Rejecting'),
    onSuccess: (outcome, { row: r, body }) => said(outcome, withReason(`${r.documentNo} rejected`, body?.comment)),
  });
  const fulfil = useMutation({
    mutationFn: decide('fulfil', 'Marking fulfilled'),
    onSuccess: (outcome, { row: r }) => said(outcome, `${r.documentNo} marked fulfilled.`),
  });
  const cancel = useMutation({
    mutationFn: decide('cancel', 'Withdrawing'),
    onSuccess: (outcome, { row: r }) => said(outcome, `${r.documentNo} withdrawn.`),
  });
  // A refused approve or reject is said in its dialog, which stays open.
  const failed = (deciding ? null : (approve.error ?? reject.error)) ?? fulfil.error ?? cancel.error;

  const rows = data?.items ?? [];

  // The open demand is read on its own, not out of the list: a decision can
  // take it out of the filter ("Open" no longer shows a rejected one), and the
  // page should stay on it, showing the decision.
  const detailQuery = useQuery({
    queryKey: ['inv', 'demands', 'detail', openKey],
    queryFn: async () => (await api.get(`/api/v1/inventory/demands/${openKey}` as '/api/v1/inventory/demands/{id}')) as unknown as DemandRow,
    enabled: openKey !== null,
  });
  const { data: detail, error: detailError, isFetching: detailFetching } = detailQuery;

  /** Moving between the list and a demand: what was said on one is not news on the other. */
  function show(key: string | null) {
    setOpenKey(key);
    if (key) setSelectedKey(key);
    setNote(null);
    for (const m of [approve, reject, fulfil, cancel]) m.reset();
  }

  function actions(row: DemandRow): DesktopAction[] {
    const waiting = row.status === 'PENDING';
    return [
      {
        label: 'Approve', icon: 'approve',
        onSelect: () => setDeciding({ mode: 'approve', row }),
        allowed: can('inv.demand.approve'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot decide on it' : 'Only a waiting demand can be approved',
      },
      {
        label: 'Reject', icon: 'reject',
        onSelect: () => setDeciding({ mode: 'reject', row }),
        allowed: can('inv.demand.reject'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot decide on it' : 'Only a waiting demand can be rejected',
      },
      {
        label: 'Mark fulfilled', icon: 'post',
        onSelect: () => fulfil.mutate({ row }),
        allowed: can('inv.demand.update'),
        enabled: row.status === 'APPROVED',
        reason: 'Only an approved demand can be marked fulfilled',
      },
      {
        label: 'Withdraw', icon: 'reverse',
        onSelect: () => cancel.mutate({ row }),
        allowed: can('inv.demand.delete') && row.raisedByYou,
        enabled: waiting,
        reason: 'Only a demand nobody has decided on can be withdrawn',
        separatorBefore: true,
      },
    ];
  }

  const rowActions = (row: DemandRow): DesktopAction[] => [
    { label: 'Open', icon: 'details', shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
    ...actions(row).map((a, i) => (i === 0 ? { ...a, separatorBefore: true } : a)),
  ];

  const columns: Array<GridColumn<DemandRow>> = [
    { key: 'doc', header: 'Demand', width: 150, render: (r) => <span className="withicon"><AppIcon kind="demand" size={14} /><span className="mono">{r.documentNo}</span></span> },
    { key: 'raised', header: 'Raised', width: 100, render: (r) => shortDate(r.raisedAt) },
    { key: 'store', header: 'Store', width: 100, render: (r) => <span className="dim">{r.storeCode}</span> },
    { key: 'item', header: 'Item', width: 240, render: (r) => <><span className="mono dim">{r.skuCode}</span> {r.skuName}</> },
    { key: 'qty', header: 'Asked for', align: 'end', width: 130, render: (r) => <AskedFor demand={r} /> },
    { key: 'onHand', header: 'On hand now', align: 'end', width: 110, render: (r) => <span className="dim">{quantity(r.onHand, r.uomPrecision)}</span> },
    { key: 'neededBy', header: 'Needed by', width: 110, render: (r) => (r.neededBy ? shortDate(r.neededBy) : <span className="dim">Soon</span>) },
    { key: 'by', header: 'Raised by', width: 140, render: (r) => <span className="dim">{r.raisedBy ?? '—'}</span> },
    {
      key: 'status', header: 'Status', width: 120,
      render: (r) => {
        const said = decisionTitle(r);
        return said ? <span title={said}><DemandStatus status={r.status} /></span> : <DemandStatus status={r.status} />;
      },
    },
  ];

  const toolbarActions: DesktopAction[] = [
    { label: 'New demand', icon: 'demand', primary: true, onSelect: () => setRaising(true), allowed: can('inv.demand.create') },
  ];

  const decisionDialog = deciding && (
    <DemandDecisionDialog
      key={`${deciding.mode}-${deciding.row.id}`}
      mode={deciding.mode}
      demand={deciding.row}
      onSubmit={(body) => (deciding.mode === 'approve' ? approve : reject).mutateAsync({ row: deciding.row, body })}
      onClose={() => {
        setDeciding(null);
        // What it was refused with was said in the dialog; not again behind it.
        if (approve.isError) approve.reset();
        if (reject.isError) reject.reset();
      }}
    />
  );

  if (openKey) {
    const openRow = rows.find((r) => r.id === openKey);
    // As loaded, or as the list had it while it loads or when it cannot.
    const demand = detail ?? openRow;
    const busy = [approve, reject, fulfil, cancel].some((m) => m.isPending) || detailFetching;
    return (
      <DocumentFrame
        onBack={() => show(null)}
        backLabel="Back to Stock Demands"
        title={
          demand && (
            <>
              <h2 className="withicon">
                <AppIcon kind="demand" size={18} />
                <span className="mono">{demand.documentNo}</span>
              </h2>
              <DemandStatus status={demand.status} />
              <span className="muted">
                {demand.storeCode} · raised {shortDate(demand.raisedAt)}
              </span>
            </>
          )
        }
        actions={
          demand &&
          actions(demand)
            .filter((a) => a.allowed && a.enabled !== false)
            .map((a) => (
              <button
                key={a.label}
                type="button"
                className={`btn btn--withicon${a.label === 'Approve' || a.label === 'Mark fulfilled' ? ' btn--primary' : ''}`}
                disabled={busy}
                onClick={a.onSelect}
              >
                {a.icon && <AppIcon kind={a.icon} size={15} />}
                {a.label}
              </button>
            ))
        }
      >
        {note && <OkNote message={note} />}
        {failed && <ErrorNote message={errorMessage(failed, 'Action failed')} />}
        {detailError && !demand && <ErrorNote message={detailError.message} />}
        {!demand && !detailError && <LoadingNote what="Loading the demand…" />}
        {demand && <DemandDocument demand={demand} />}
        {decisionDialog}
      </DocumentFrame>
    );
  }

  return (
    <div className="workbench">
      <div className="workbench__main">
        <div className="filterbar">
          <Select
            label="Show"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setSkip(0);
            }}
            placeholder=""
            options={FILTERS}
          />
        </div>
        <ActionBar actions={toolbarActions} />

        {note && <OkNote message={note} />}
        {failed && <ErrorNote message={errorMessage(failed, 'Action failed')} />}
        {error && <ErrorNote message={error.message} />}

        <DataGrid
          id="inventory-demands"
          label="Stock demands"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onOpen={(row) => show(row.id)}
          openOnClick
          actions={rowActions}
          loading={isPending && !isOfflineGap(list)}
          empty={isOfflineGap(list) ? NOT_ON_THIS_PC : status === 'open' ? 'No open demands.' : 'No demands.'}
        />
        <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={setSkip} />
      </div>
      {decisionDialog}
      {raising && (
        <DemandDialog
          onClose={() => setRaising(false)}
          onSaved={(message) => {
            setRaising(false);
            setNote(message);
          }}
        />
      )}
    </div>
  );
}

/** "80 BAG", or "80 → 50 BAG" once approved for another amount. */
function AskedFor({ demand: r }: { demand: DemandRow }) {
  // Missing too on a list kept on this PC from before amounts could change.
  if (!r.approvedQty) return r.qty ? <>{amountOf(r, r.qty)}</> : <span className="dim">Not given</span>;
  const asked = r.qty ? quantity(r.qty, r.uomPrecision) : 'Not given';
  return (
    <span title={`Asked for ${r.qty ? amountOf(r, r.qty) : 'no amount'}, approved ${amountOf(r, r.approvedQty)}`}>
      <span className="dim">{asked} →</span> {amountOf(r, r.approvedQty)}
    </span>
  );
}

/** A demand's own screen: who asked for what, where the decision stands, and its history. */
function DemandDocument({ demand }: { demand: DemandRow }) {
  const amount = (qty: string) => amountOf(demand, qty);
  // What is coming: what was approved, else what was asked for.
  const agreed = demand.approvedQty ?? demand.qty;
  const short = agreed !== null && Number(demand.onHand) < Number(agreed);
  const rejected = demand.status === 'REJECTED';
  return (
    <>
      {demand.status === 'PENDING' && (
        <p className="docpage__note" role="note">
          {demand.raisedByYou
            ? 'Waiting for a decision. You raised it, so someone else decides. You can withdraw it until then.'
            : 'Waiting for a decision.'}
        </p>
      )}
      {demand.status === 'APPROVED' && (
        <p className="docpage__note" role="note">
          {demand.approvedQty
            ? `Approved for ${amount(demand.approvedQty)}${demand.qty ? ` (asked for ${amount(demand.qty)})` : ''}. `
            : 'Approved. '}
          Mark it fulfilled once the stock has come in.
        </p>
      )}
      {(rejected || demand.status === 'CANCELLED') && (
        <p className="docpage__note docpage__note--alert" role="note">
          {rejected ? 'Rejected' : 'Withdrawn'}
          {demand.decidedAt ? ` on ${shortDate(demand.decidedAt)}` : ''}
          {demand.decidedBy ? ` by ${demand.decidedBy}` : ''}.
          {rejected && (demand.decisionComment ? ` Reason: ${demand.decisionComment}` : ' No reason given.')}
        </p>
      )}

      <div className="tiles">
        <Tile label="Asked for" value={demand.qty ? amount(demand.qty) : 'Not given'} />
        {demand.approvedQty && <Tile label="Approved" value={amount(demand.approvedQty)} />}
        <Tile label="On hand now" value={amount(demand.onHand)} tone={short ? 'warn' : undefined} />
        <Tile label="Needed by" value={demand.neededBy ? shortDate(demand.neededBy) : 'Soon'} />
        <Tile label="Store" value={demand.storeCode} />
      </div>

      <section className="stack docpage__section">
        <h3 className="formsection">Demand</h3>
        <dl className="detaillist">
          <dt>Item</dt>
          <dd>
            <span className="mono">{demand.skuCode}</span> {demand.skuName}
            {demand.skuNameUr && <span className="urdu"> · {demand.skuNameUr}</span>}
          </dd>
          <dt>Store</dt>
          <dd>
            {demand.storeCode} — {demand.store}
          </dd>
          <dt>Raised</dt>
          <dd>
            {shortDate(demand.raisedAt)}
            {demand.raisedBy && ` by ${demand.raisedBy}`}
            {demand.raisedByYou && <span className="muted"> (you)</span>}
          </dd>
          <dt>Note</dt>
          <dd>{demand.note ?? '—'}</dd>
          {demand.decidedAt && demand.status !== 'CANCELLED' && (
            <>
              <dt>Decided</dt>
              <dd>
                {shortDate(demand.decidedAt)}
                {demand.decidedBy && ` by ${demand.decidedBy}`}
              </dd>
            </>
          )}
          {demand.fulfilledAt && (
            <>
              <dt>Fulfilled</dt>
              <dd>{shortDate(demand.fulfilledAt)}</dd>
            </>
          )}
          {demand.decisionComment && (
            <>
              <dt>{rejected ? 'Reason' : 'Comment'}</dt>
              <dd>{demand.decisionComment}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">History</h3>
        <HistoryPanel path={`/api/v1/inventory/demands/${demand.id}/history`} />
      </section>
    </>
  );
}
